<template>
  <div class="row items-start justify-between">
    <div>
      <h6 class="q-my-none">{{ props.name }}</h6>
      <p class="q-my-none text-subtitle">{{ props.address }}</p>
    </div>
    <q-btn
      flat
      dense
      icon="location_on"
      color="primary"
    />
  </div>

  <div ref="mapEl" class="map"></div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useYandexMaps } from '@/utils/useYandexMaps';

declare global {
  interface Window {
    ymaps: any
  }
}

interface Props {
  name: string
  address: string
  buildingCoords: [number, number]
  entranceCoords?: [number, number] | null
}

const props = defineProps<Props>();

const mapEl = ref<HTMLElement | null>(null);
let map: any = null;

const { load } = useYandexMaps();

onMounted(async () => {
  await load();

  window.ymaps.ready(() => {
    map = new window.ymaps.Map(mapEl.value, {
      center: props.buildingCoords,
      zoom: 16,
      controls: ['zoomControl', 'geolocationControl'],
    });

    const clusterer = new window.ymaps.Clusterer();

    const buildingPlacemark = new window.ymaps.Placemark(
      props.buildingCoords,
      {
        balloonContent: `
          <strong>${props.name}</strong><br/>
          ${props.address}<br/><br/>
          ${props.entranceCoords
    ? '<button id="route-btn">Построить маршрут до входа</button>'
    : '<button id="route-btn">Построить маршрут</button>'
}
        `,
      },
      {
        preset: 'islands#greenMedicalIcon',
      },
    );

    let entrancePlacemark: any = null;

    if (props.entranceCoords) {
      entrancePlacemark = new window.ymaps.Placemark(
        props.entranceCoords,
        {
          balloonContent: `<strong>Вход в ${props.name}</strong>`,
        },
        {
          preset: 'islands#blueCircleDotIcon',
        },
      );

      clusterer.add(entrancePlacemark);
    }

    clusterer.add(buildingPlacemark);
    map.geoObjects.add(clusterer);

    buildingPlacemark.events.add('balloonopen', () => {
      const btn = document.getElementById('route-btn');

      if (btn) {
        btn.onclick = () => {
          window.ymaps.geolocation.get().then((res: any) => {
            const userCoords = res.geoObjects.position;

            const targetCoords = props.entranceCoords ?? props.buildingCoords;

            const route = new window.ymaps.multiRouter.MultiRoute({
              referencePoints: [userCoords, targetCoords],
              params: {
                routingMode: 'auto',
              },
            });

            map.geoObjects.add(route);
          });
        };
      }
    });
  });
});

onUnmounted(() => {
  if (map) {
    map.destroy();
    map = null;
  }
});
</script>

<style scoped>
.map {
  width: 100%;
  height: 400px;
  margin-top: 16px;
  margin-bottom: 16px;
}
</style>
